/**
 * Single source of truth for what the product's modules ARE, in marketing copy.
 *
 * It used to back a set of /modules/<slug> landing pages; those were removed,
 * so a row here is content rather than a route. Consumed by:
 *  - src/app/modules/page.tsx      → the /modules hub, the only URL in the branch
 *  - src/lib/howItWorksVideos.ts   → names the modules each chapter touches
 *  - src/lib/catalog/suites.ts     → the /all-module suites join on these slugs
 *
 * Order and grouping follow the STAGES a job actually passes through — enquiry,
 * quotation, site, material, wages, money — because that is how the hub is laid
 * out and how a builder describes their own business. `stage` is the join key.
 */
export interface MarketingModule {
  /** Stable key for this module. No longer a URL — see the note above. */
  slug: string;
  /** Display name. */
  label: string;
  /** Which stage of a job this module does the work for. */
  stage: ModuleStageId;
  /**
   * What this module does, in one operational line. Deliberately short: on the
   * hub it sits on a single row under the stage heading, not inside a card.
   */
  role: string;
}

export type ModuleStageId = 'enquiry' | 'quotation' | 'site' | 'material' | 'wages' | 'money';

export const MARKETING_MODULES: MarketingModule[] = [
  { slug: 'leads',           label: 'Leads (CRM)',           stage: 'enquiry',   role: 'Website, WhatsApp, Meta ads and referrals land in one pipeline with a next action on every lead.' },

  { slug: 'estimates',       label: 'Estimating & BOQ',      stage: 'quotation', role: 'Build the bill of quantities from your own rates, add margin and GST, print it on your letterhead.' },
  { slug: 'packages',        label: 'Packages & Quotations', stage: 'quotation', role: 'Turnkey packages priced per square foot, each with the specification sheet the client asks for.' },

  { slug: 'sites',           label: 'Sites & Projects',      stage: 'site',      role: 'The won job becomes a site with its budget, stage payment plan, team and add-ons already set.' },
  { slug: 'schedule',        label: 'Schedule & Gantt',      stage: 'site',      role: 'Task dependencies, the critical path, and a finish date that moves as crews mark progress.' },

  { slug: 'vendors',         label: 'Vendors',               stage: 'material',  role: 'One record per supplier — GSTIN, payment terms, bill history and what you still owe them.' },
  { slug: 'purchase-orders', label: 'Purchase Orders',       stage: 'material',  role: 'Raise a PO with GST and HSN, route it for approval, match the bill back to the order.' },
  { slug: 'bills',           label: 'Vendor Bills',          stage: 'material',  role: 'Per-line GST and TDS, verify before you pay, payments allocated oldest bill first.' },
  { slug: 'inventory',       label: 'Inventory & Stock',     stage: 'material',  role: 'Goods received, issued to site, valued at a moving average — on-hand computed from movements.' },

  { slug: 'employees',       label: 'Employees & HR',        stage: 'wages',     role: 'Office staff and site crews, with departments, designations and who is posted where.' },
  { slug: 'attendance',      label: 'Attendance',            stage: 'wages',     role: 'The daily muster, marked from the field, including leave, holidays and loss of pay.' },
  { slug: 'payroll',         label: 'Labour Payroll',        stage: 'wages',     role: 'Daily-wage and monthly runs with overtime, posted to the ledger against the right site.' },

  { slug: 'ledger',          label: 'Ledger',                stage: 'money',     role: 'Append-only, paise-accurate books — client invoices, receipts, payouts, cash and bank.' },
  { slug: 'reports',         label: 'Reports & Analytics',   stage: 'money',     role: 'Cash on hand, per-site profit, vendor aging and the lead funnel. All of it exports.' },
];

export interface ModuleStage {
  id: ModuleStageId;
  /** What happens in the business at this stage, in the builder's own words. */
  title: string;
  /** Two sentences of context, read before the module rows under it. */
  body: string;
  /** Real app capture in /public/marketing/suites, or null where none is fit to ship. */
  shot: { file: string; alt: string; ratio: number } | null;
}

/**
 * The stages, in order. A job runs top to bottom.
 *
 * `ratio` crops each capture to its top band (aspect-ratio + overflow hidden).
 * Native captures are 2686×1678; a ratio of 2686/(1678 × fraction) keeps the
 * first `fraction` of the screen. That is not styling for its own sake — it
 * removes the dead white space at the foot of several captures, and on
 * `estimates.png` it crops away phone numbers that came from working data
 * rather than the demo seed, and on `reports.png` it stops above a stat row
 * whose month-on-month deltas (+4244%, +104300%) are arithmetically true on
 * seeded data and read as invented to everyone else.
 *
 * `shot: null` on two stages is deliberate: the `projects.png` and `people.png`
 * captures both fail the checklist in public/marketing/suites/README.md (junk
 * rows, zeroed metrics, a wall of red "will be LOP"). Shipping a bad capture is
 * worse than shipping none, so those stages read as a denser module list until
 * they are re-shot.
 */
export const MODULE_STAGES: ModuleStage[] = [
  {
    id: 'enquiry',
    title: 'An enquiry comes in',
    body: 'Someone calls about a plot in Saravanampatti. Today that is a name in a notebook, and a follow-up that happens if somebody remembers. Here it arrives in a pipeline that already knows who owns it and what happens next.',
    shot: { file: 'crm.png', alt: 'The Leads screen: pipeline counts, follow-up dates and the source of every enquiry', ratio: 2.13 },
  },
  {
    id: 'quotation',
    title: 'You put a price on it',
    body: 'Either way you quote — a bill of quantities at your own rates, or a turnkey package at a rate per square foot — the number that goes to the client is the number that becomes the budget when you win.',
    shot: { file: 'estimates.png', alt: 'The Estimates screen: drafts, quotes awaiting the client, and what was accepted this month', ratio: 4.71 },
  },
  {
    id: 'site',
    title: 'The job becomes a site',
    body: 'Nothing is re-typed. The estimate you won carries its budget, its stage payment plan and its scope across, and the schedule tells you which delay actually moves your handover date and which one has float.',
    shot: null,
  },
  {
    id: 'material',
    title: 'Material starts moving',
    body: 'Cement is ordered, received, issued to a site and billed — four events that normally live in four places. Each one is linked to the order before it, so a bill nobody ordered and a second payment on the same bill both stop being possible.',
    shot: { file: 'procurement.png', alt: 'The Vendors screen: outstanding by supplier, bills recorded and paid to date', ratio: 2.22 },
  },
  {
    id: 'wages',
    title: 'The crew gets paid',
    body: 'Mason, helper, maistry. The muster is marked on a phone at the site gate, and that same day count is what payroll runs on — so labour cost lands on the site that used the labour, not in a lump at month end.',
    shot: null,
  },
  {
    id: 'money',
    title: 'You find out if you made money',
    body: 'Every entry above has already written itself into the books. Which is why the profit on a plot is a number you can look at on a Tuesday, rather than something your auditor tells you in September.',
    shot: { file: 'reports.png', alt: 'The Reports screen and its report tabs: spend, cash flow, payables, receivables, P&L, sites, TDS, RERA and Tally', ratio: 6.4 },
  },
];
